import React, {
  createContext,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";

import Toast, { ToastVariant } from "@/ui/Toast";

type Variant = ToastVariant;

type SnackbarState = {
  open: boolean;
  message: string;
  variant: Variant;
  duration?: number;
};

export const SnackbarContext = createContext({
  openSnackbar: (message: string, variant: Variant, duration?: number) => {
    console.log("openSnackbar", message, variant, duration);
  },
  closeSnackbar: () => {},
});

export const SnackbarProvider = ({ children }: any) => {
  const [snackbarState, setSnackbarState] = useState<SnackbarState>({
    open: false,
    message: "",
    variant: "success",
    duration: 3000,
  });

  // We need the snackbar to appear above SAM's chat interface (z-index: 1400).
  // The style is only injected once and cleaned up on unmount to avoid memory leaks.
  useEffect(() => {
    const styleId = "snackbar-zindex-override";

    // Only inject if not already present
    if (!document.getElementById(styleId)) {
      const style = document.createElement("style");
      style.id = styleId;
      style.innerHTML = `
        .MuiSnackbar-root {
          z-index: 1500 !important;
        }
      `;
      document.head.appendChild(style);
    }

    // Cleanup: Remove style on unmount to prevent memory leaks
    return () => {
      const existingStyle = document.getElementById(styleId);
      if (existingStyle && existingStyle.parentNode) {
        existingStyle.parentNode.removeChild(existingStyle);
      }
    };
  }, []);

  const openSnackbar = useCallback(
    (message: any, variant: any, duration?: number) => {
      // Close first to force re-trigger if already open
      setSnackbarState((prev) => {
        if (prev.open) {
          // Already open - close briefly then reopen
          setTimeout(() => {
            setSnackbarState({
              open: true,
              message,
              variant,
              duration: duration || 3000,
            });
          }, 50);
          return { ...prev, open: false };
        }
        // Not open - just open normally
        return {
          open: true,
          message,
          variant,
          duration: duration || 3000,
        };
      });
    },
    []
  );

  const closeSnackbar = useCallback(() => {
    setSnackbarState((prevState) => ({ ...prevState, open: false }));
  }, []);

  const contextValue = useMemo(
    () => ({
      openSnackbar,
      closeSnackbar,
    }),
    [openSnackbar, closeSnackbar]
  );

  return (
    <SnackbarContext.Provider value={contextValue}>
      {children}
      <Toast
        open={snackbarState.open}
        message={snackbarState.message}
        variant={snackbarState.variant}
        onClose={closeSnackbar}
        duration={snackbarState.duration}
      />
    </SnackbarContext.Provider>
  );
};
